import axios, {AxiosResponse} from 'axios'

const API_URL = process.env.REACT_APP_THEME_API_URL

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface WarehouseOption {
  warehouse_id: number
  warehouse_name: string
  status?: string
}

export interface ProductOption {
  product_id: number
  product_name: string
  status?: string
}

export interface InventoryMetrics {
  available_stock: number
  stock_valuation: number
  valuation_per_unit: number
}

// ─── Shared Options APIs ──────────────────────────────────────────────────────

export const getWarehouseOptions = async (
  companyId: string
): Promise<WarehouseOption[] | undefined> => {
  return await axios
    .get(`${API_URL}/warehouse/list/${companyId}`)
    .then((response: AxiosResponse<WarehouseOption[]>) => response.data)
    .then((response: WarehouseOption[]) => response.filter((item) => item.status === 'active'))
}

export const getProductOptions = async (
  companyId: string
): Promise<ProductOption[] | undefined> => {
  return await axios
    .get(`${API_URL}/product-catalogue/list/${companyId}`)
    .then((response: AxiosResponse<ProductOption[]>) => response.data)
    .then((response: ProductOption[]) => response.filter((item) => item.status === 'active'))
}

export const getCurrentInventoryMetrics = async (
  companyId: string,
  warehouseId: number,
  productId: number
): Promise<InventoryMetrics | undefined> => {
  return await axios
    .get(`${API_URL}/voucher-inventory/${companyId}/${warehouseId}/${productId}/current`)
    .then((response: AxiosResponse<InventoryMetrics>) => response.data)
}

// ─── Initial Assignment ───────────────────────────────────────────────────────

export const createStockAssignment = async (body: any): Promise<{detail: string} | undefined> => {
  try {
    const response = await axios.post(`${API_URL}/stock-assignment/create`, body)
    return response.data
  } catch (error: any) {
    if (error.response) {
      throw error.response.data
    }
  }
}

// ─── Stock In ─────────────────────────────────────────────────────────────────

export const createStockIn = async (body: any): Promise<{detail: string} | undefined> => {
  try {
    const response = await axios.post(`${API_URL}/stock-in/create`, body)
    return response.data
  } catch (error: any) {
    if (error.response) {
      throw error.response.data
    }
  }
}

export const getStockInList = async (companyId: string): Promise<any[] | undefined> => {
  return await axios
    .get(`${API_URL}/stock-in/${companyId}/list`)
    .then((response: AxiosResponse<any[]>) => response.data)
    .then((response: any[]) => response)
}

// ─── Stock Out ────────────────────────────────────────────────────────────────

export const createStockOut = async (body: any): Promise<{detail: string} | undefined> => {
  try {
    const response = await axios.post(`${API_URL}/stock-out/create`, body)
    return response.data
  } catch (error: any) {
    if (error.response) {
      throw error.response.data
    }
  }
}

// ─── Stock Transfer ───────────────────────────────────────────────────────────

export const createStockTransfer = async (body: any): Promise<{detail: string} | undefined> => {
  try {
    const response = await axios.post(`${API_URL}/stock-transfer/create`, body)
    return response.data
  } catch (error: any) {
    if (error.response) {
      throw error.response.data
    }
  }
}

// ─── Stock Adjustment ─────────────────────────────────────────────────────────

export const createStockAdjustment = async (body: any): Promise<{detail: string} | undefined> => {
  try {
    const response = await axios.post(`${API_URL}/stock-adjustment/create`, body)
    return response.data
  } catch (error: any) {
    if (error.response) {
      throw error.response.data
    }
  }
}
